/**
 * Base64 Image Converter Tool
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

function parseDataUrl(input: string): { mimeType: string; base64: string } | null {
  const trimmed = String(input ?? '').trim();
  const m = trimmed.match(/^data:([^;]+);base64,(.*)$/s);
  if (!m) return null;
  return { mimeType: m[1]!, base64: m[2]!.replace(/\s+/g, '') };
}

export const Base64ImageConverterTool: ITransformerTool = {
  id: 'base64-image-converter',
  name: 'Base64 Image Converter',
  description: 'Convert between base64 image data and data URLs',
  version: '1.0.0',
  type: 'transformer',
  category: 'Encoders & Decoders',
  keywords: ['base64', 'image', 'data url', 'convert'],

  supportedInputTypes: [SEMANTIC_TYPES.TEXT, SEMANTIC_TYPES.BASE64],
  outputType: SEMANTIC_TYPES.JSON,

  attributes: [
    {
      name: 'mimeType',
      type: 'select',
      label: 'MIME Type',
      defaultValue: 'image/png',
      options: ['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml'],
      description: 'Used when input is raw base64',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const text = String(input ?? '').trim();
    if (!text) return JSON.stringify({ mimeType: '', base64: '', dataUrl: '' }, null, 2);

    const parsed = parseDataUrl(text);
    if (parsed) {
      return JSON.stringify(
        {
          mimeType: parsed.mimeType,
          base64: parsed.base64,
          dataUrl: `data:${parsed.mimeType};base64,${parsed.base64}`,
        },
        null,
        2,
      );
    }

    const base64 = text.replace(/\s+/g, '');
    if (!/^[A-Za-z0-9+/]+={0,2}$/.test(base64)) {
      throw ToolError.validation('Input must be an image data URL or base64');
    }

    const mimeType = String(config.mimeType ?? 'image/png');
    return JSON.stringify(
      {
        mimeType,
        base64,
        dataUrl: `data:${mimeType};base64,${base64}`,
      },
      null,
      2,
    );
  },
};
